import gql from 'graphql-tag';
import { OFFER_FRAGMENT } from 'core/fragments/offer';
import {
  FARM_LOCATIONS_FRAGMENT,
  LOSS_HISTORY_FRAGMENT,
  FARM_LIABILITY_COVERAGE_FRAGMENT,
  FARM_VERIFICATION_FRAGMENT
} from 'core/fragments/farm';

export const Address = gql`
  fragment Address on AddressDetails {
    address
    address2
    unit
    country
    city
    state
    zip
  }
`;

export const People = gql`
  fragment People on PersonDetails {
    id
    age
    isPrimary
    isCoApplicant
    firstName
    middleName
    lastName
    suffix
    gender
    excludeDriver
    maritalStatus
    takenCourse
    courseDiscountConditionCheck
    highestEducation
    driverStatus
    extendedNonOwnedCoverage
    # CFM auto Claims Free / Violation Free discounts (#35626); derived server-side, read-only.
    autoClaimsFree
    autoViolationFree
    dateOfBirth
    driversLicenseNumber
    driversLicenseState
    skipLicenseNumValidation
    ageFirstLicensed
    relationshipToInsured
    fullTimeStudent
    goodStudent
    exclusionReason
    removedReason
    removedBy
    removedTimestamp
    schoolName
    addDate
    communityDriveParticipation
    communityDriveScore
    everCommunityDriveParticipation
    needSR22
    lapsedLicense
    hasViolationsBlockingUmbrella
    hasMajorViolationBlockingUmbrella
    hasAtFaultAccidentBlockingUmbrella
    insuranceScore {
      homeTotal
      autoTotal
      total
      homeNoScore
      autoNoScore
    }
    insuranceScoreOverride
    insuranceScoreOverrideValue
    schoolLocation {
      ...Address
    }
    autoViolationDates
    waivedPIPWL
    pipEligible
    pipOptOut
    pipHasQHC
    assignedCar
    userAssignedCar
    autoViolations {
      AAF
      AFM
      ANC
      ANO
      ASW
      BOT
      CCW
      CML
      CMP
      CMPTV
      CMU
      CRD
      DEQ
      DEV
      DR
      DWI
      FAR
      FDL
      FEL
      FFR
      FLE
      FRA
      FTC
      FTY
      HOM
      IBK
      IP
      IT
      LDL
      LIC
      LTS
      MAJ
      MMV
      NAF
      NFX
      PUA
      PUN
      REF
      RKD
      SAF
      SCH
      SLV
      SPD
      SUS
      TMP
      UDR
      WSR
    }
  }
`;

export const PolicyFragment = gql`
  fragment PolicyFragment on Policy {
    id
    offerId
    policyType
    paymentType
    paymentMode
    directBillPreferredPaymentMethod
    isBix
    isAgentSold
    source
    canEndorse
    wasRenewedFromImportedTerm
    supplementalDocuments
    lastInspectionDate
    importedData {
      conversionMetadata {
        policyNumber
        carrier
        sourceComments
        policyTypeConversionNote
      }
    }
    homeInspectionRepairsRequested
    salesRep
    branchPlusLinkedOfferId
    bindRep
    paymentMethod
    premiumBreakdown
    webUserId
    policyDetails {
      monthsAtCurrentAddress
      autoCoverage {
        policyLimitBIPD
        policyLimitUMBI
        policyLimitUIMBI
        policyLimitPIP
        policyLimitUMPD
        policyLimitMedicalPayments
        policyLimitLPD
        petInjury
        coverageEducatorAdvantage
        coverageOffPremisesExpanded
        policyLimitPIPME
        policyLimitPIPWL
        policyLimitPIPACR
        policyLimitPPI
        policyLimitIncomeLoss
        policyLimitAccidentalDeath
        policyLimitFuneralBenefits
        policyLimitExtraMedBenefits
        policyLimitTortOption
        policyLimitComboFBP
        policyLimitGuestPIP
        policyLimitNoFaultPIP
        uimAddedOn
        skipPDValidation
      }
      branchPlusPolicies {
        policyType
        policyCarrier
        policyNumber
        attestationAgent
      }
      homeCoverage {
        coverageA
        limitOverride
        limitOverrideReason
        limitOverrideBy
        limitOverrideAt
        limitOverrideRemovedBy
        limitOverrideRemovedAt
        deductibleAllOther
        deductibleWindHail
        deductibleHurricane
        coverageCPctOfA
        coverageBPctOfA
        coverageX
        coverageJ
        coverageWaterBackup
        coverageG
        interestedInSeparateWindHail
        externalPolicyId
      }
      drivers {
        ...People
      }
      nonDrivers {
        ...People
      }
      people {
        ...People
      }
      removedDrivers {
        ...People
      }
      cars {
        addDate
        VIN
        vehicleNumber
        year
        make
        model
        bodyType
        primaryUse
        purchaseDate
        brandedTitle
        brandedTitleDescription
        garageLocation {
          ...Address
        }
        isGarageAddressRuleExceptionApproved
        deductibleCollision
        deductibleComprehensive
        limitUMPD
        limitRental
        limitACPE
        customEquipmentDescription
        limitIncomeLoss
        roadsideAssistance
        extendedNonOwnedCoverage
        solelyOwnedByApplicant
        statedAmount
        jointOwner
        jointOwnerName
        jointOwnerAddress {
          address
          address2
          city
          state
          zip
        }
        jointOwnerSameHousehold
        jointOwnerRelated
        jointOwnerRelationship
        ownership
        rideSharing
        umuimRejected
        confirmed
        aebStatus
        aebConfirmed
        antiTheftStatus
        antiTheftConfirmed
        singleAirbagStatus
        singleAirbagConfirmed
        doubleAirbagStatus
        doubleAirbagConfirmed
        luxury
        coverageLoan
        symbolMake
        symbolModel
        symbolStyle
        symbolAux
        annualMilesDriven
        verifiedAnnualMilesDriven
        reportedAnnualMiles
        hasOutstandingRecall
        symbolPGS
        symbolBI
        symbolPD
        symbolMed
        symbolPIP
        symbolColl
        symbolComp
        symbolUM
        vehicleTypeCode
        vehicleHorsepowerCode
        convertible
        vehicleSymbolGroup
        rapaLiabilitySymbol1
        rapaLiabilitySymbol2
        rapaPhysDamSymbol1
        rapaPhysDamSymbol2
        rapaBodilyInjurySymbol
        rapaPropertyDamageSymbol
        rapaMedPaySymbol
        rapaComprehensiveSymbol
        rapaCollisionSymbol
      }
      trailers {
        VIN
        vehicleNumber
        type
        make
        model
        primaryUse
        year
        value
        contents
        purchaseDate
        garageLocation {
          ...Address
        }
        deductibleCollision
        deductibleComprehensive
        confirmed
        coverageLoan
        addDate
      }
      includeUmbrella
      includeRenters
      includeEarthquake
      includeFlood
      earthquakeCoverage {
        policyId
      }
      floodCoverage {
        policyId
      }
      attachedHomeowners
      attachedRenters
      attachedAuto
      attachedCondo
    }
    overrides {
          canEditUneditablePropertyDetails
          allowExcludingDrivers
          overridesForFeFormValidation {
            skipBogusLoanNumberCheck
            skipUmbrellaBIPDValidation
          }
          overridesForUW {
            code
            overridingAgent
            type
          }
          rc1 {
            itemsChanged
          }
        }
    billingDayOfMonth
    billingHoldUntil
    billingHoldReason
    billingHoldBlocksCancellations
    billingHoldPlacedBy
    billingHoldPlacedAt
    blockCreditCheckAtRenewal
    rateControlDate
    effectiveDate
    endDate
    fullTermPolicyEndDate
    inceptionDate
    autoRenew
    sendPaperDocuments
    renewalCreditPull
    renewalPaymentType
    renewalPaymentMethod
    renewalVariables {
      allow300KCSL
      allowForcedNonRenewal
      allowUmbrella
      allowMonthlyPayments
    }
    state
    term
    version
    premium
    surplusContribution
    rewriteReason
    rewrittenToPolicyId
    cancelReason
    nonRenewReason
    cancelled
    nonRenewed
    fullTermCancel
    stripeCustomerId
    otherStripeCustomers {
      stripeId
      active
      paymentMethod
    }
    defaultCreditCard {
      id
      brand
      last4
      bankName
    }
    defaultBankAccount {
      id
      routingNumber
      accountHolder
      accountType
    }
    defaultEscrowAccount {
      mortgageHolderName
      loanNumber
      mortgageHolderAddress {
        address
        address2
        country
        state
        city
        zip
      }
      mortgageHolderEmail
      mortgageHolderFax
    }
    accountDetails {
      accountHolder
      accountType
      routingNumber
      accountNumber
    }
    bankAccounts {
      accountHolder
      accountType
      routingNumber
      accountNumber
    }
    creditCards {
      id
      brand
      last4
    }
    additionalParties {
      id
      name
      email
      relationship
      lossPayee
      associatedEntities
      address {
        address
        address2
        unit
        country
        city
        state
        zip
      }
      loanNumber
      VIN
      VINs
      trusteeNames
      trustees {
        name
        address {
          address
          address2
          city
          state
          zip
        }
      }
    }
    farmInterestedParties {
      id
      isDeleted
      name
      email
      relationship
      description
      isResident
      entityType
      fo41 {
        description
        locations
        risks
        equipment
        sppItems
        rpsItems
      }
      associatedEntities {
        entityType
        locationId
        riskId
        itemId
      }
      address {
        address
        address2
        unit
        country
        city
        state
        zip
      }
    }
    fees {
      type
      description
      amount
      appliedDate
    }
    fromStaff
    offer {
      ...OfferFragment
    }
    versionHistory {
      version
      username
      updatedDateTime
      coverageChange
      itemsChanged
      itemsChangedAddendum
      effectiveDate
      internalNotes
      changeInPremium
      totalPrice
      suppressCommunications
    }
    segments {
      segmentId
      deltaDirectives
      clarionDoorId
      farmClarionDoorIds {
        liabilityClarionDoorId
        risksClarionDoorIds {
          riskId
          clarionDoorId
        }
      }
      clarionDoorRelease
      startDate
      dailyRate
      segmentDays
      segmentPremium
      entity {
        id
        type
        name
        address {
          address
          address2
          city
          state
          zip
        }
      }
      farmLocations {
        ...FarmLocationsFragment
      }
      lossHistory {
        ...LossHistoryFragment
      }
      # Rated coverage lines exactly as Clarion Door returned them. Only type/amount are selected —
      # the Discounts card needs the applied claim_free_savings total and nothing else off this
      # array, so the rest of CoverageDetails is deliberately left unfetched.
      coverages {
        type
        amount
      }
      includeFarmLiability
      farmLiabilityCoverage {
        ...FarmLiabilityCoverageFragment
      }
      farmCoverage {
        identityFraudLimit
        extraExpenseLimit
        propertyInTransitLimit
        hasEquipmentBreakdownCoverage
        propertyInCareIncreasedLimit
        fungiPropertyLimit
        fungiLiabilityLimit
        pollutantCleanupLimit
        pollutantCleanupDeductible
        machineryNotDescribedLimit
        newlyAcquiredMachineryLimit
        rentedOrBorrowedEquipmentLimit
        replacementMachineryLimit
        policyLevelAdjustmentFactor
        adjustmentRationale
        includePersonalUmbrella
        personalUmbrella {
          hasCfmAuto
          limitOfLiability
          selfInsuredRetention
          combinedSingleLimit
          excludeNamedPremises
          excludedPremises {
            address
            address2
            unit
            city
            county
            state
            zip
          }
          primaryAutomobile
          primaryAutomobileYear
          primaryAutomobileMake
          primaryAutomobileModel
          primaryAutoLiabilityLimit
          additionalAutomobiles {
            id
            year
            make
            model
          }
          numAdditionalAutomobiles
          additionalAutoLiabilityLimit
          excludeNamedAutomobile
          excludedAutomobileDescription
          youthfulDrivers {
            id
            name
            dob
            licenseNumber
            licenseState
          }
          excludeNamedDriver
          excludedDriverName
          excludedDriverDob
          antiqueClassicVehicles {
            id
            year
            make
            model
          }
          numIndividualsCaredFor
          dayCareDescription
          dayCareLocation
          hasSwimmingPool
          hasTrampoline
          additionalOwnerOccupiedResidences {
            id
            combinedSingleLimit
            addressDetails {
              address
              address2
              unit
              city
              county
              state
              zip
            }
          }
          rentalDwellings {
            id
            combinedSingleLimit
            addressDetails {
              address
              address2
              unit
              city
              county
              state
              zip
            }
          }
          hasBusinessPursuits
          businessPursuitsDescription
          businessPursuitsLocation
          businessPursuitsLimit
          watercraft {
            id
            category
            description
            horsepowerBand
            lengthBand
            speedBand
          }
          recreationalVehicles {
            id
            category
            description
            horsepower
          }
          excludeCanine
        }
      }
      autoCoverage {
        policyLimitBIPD
        policyLimitUMBI
        policyLimitUIMBI
        policyLimitPIP
        policyLimitUMPD
        policyLimitMedicalPayments
        policyLimitLPD
        petInjury
        coverageEducatorAdvantage
        coverageOffPremisesExpanded
        policyLimitPIPME
        policyLimitPIPWL
        policyLimitPIPACR
        policyLimitPPI
        policyLimitIncomeLoss
        policyLimitAccidentalDeath
        policyLimitFuneralBenefits
        policyLimitExtraMedBenefits
        policyLimitTortOption
        policyLimitComboFBP
        policyLimitGuestPIP
        policyLimitNoFaultPIP
        uimAddedOn
        skipPDValidation
      }
      scheduledPersonalProperty {
        deductible
        items {
          category
          description
          value
          breakageOption
          exTheftOption
        }
      }
      premium
      includeUmbrella
      includeRenters
      includeEarthquake
      includeFlood
      earthquakeCoverage {
        policyId
      }
      floodCoverage {
        policyId
      }
      attachedHomeowners
      attachedRenters
      attachedAuto
      attachedCondo
      umbrellaCoverage {
        highRisk
        numVehiclesTotal
        numLicensedDriversUnder25
        numMotorcyclesScooters
        numATVs
        numPersonalWatercraft
        numWatercraft
        watercraftHullLengths
        numRVs
        numRentalProperties
        uninsuredMotoristLimit
        rentalPropertyAddresses {
          ...Address
        }
        numOtherProperties
        otherPropertyAddresses {
          ...Address
        }
        presenceOfBusiness
        liabilityCoverageLimit
        limitBIOccurrence
      }
      branchPlusPolicies {
        policyType
        policyCarrier
        policyNumber
        attestationAgent
      }
      homeCoverage {
        coverageA
        limitOverride
        limitOverrideReason
        limitOverrideBy
        limitOverrideAt
        limitOverrideRemovedBy
        limitOverrideRemovedAt
        coverageIBSC
        coverageBCPctOfA
        coverageBPctOfA
        coverageCPctOfA
        coverageDPctOfA
        coverageX
        coverageY
        coverageWaterBackup
        coverageBuildingMaterialsTheft
        coverageCourseOfConstruction
        coverageExtendedPremises
        coverageEducatorAdvantage
        coverageOffPremisesExpanded
        coverageFairRental
        coveragePetBite
        coverageRSExtended
        coverageYardAndGarden
        coverageSinkhole
        coverageOilStorageTank
        coverageBP
        coverageCA
        coverageDC
        coverageDR
        coverageF
        coverageG
        coverageJ
        coverageMI
        coverageMS
        coverageSP
        coverageST
        coverageEBSL
        coverageFortifiedRoof
        homeownerProtection
        deductibleAllOther
        deductibleHurricane
        deductibleWaterBackup
        deductibleWindHail
        windHailExclusion
        minimumDeductibleValue
        minimumWindHailDeductible
        minimumWindHailDeductibleValue
        minimumHurricaneDeductible
        minimumHurricaneDeductibleValue
        interestedInSeparateWindHail
        externalPolicyId
      }
      condoCoverage {
        coverageC
        coverageA
        coverageBCPctOfC
        coverageDPctOfC
        coverageEBSL
        coverageX
        coverageY
        coverageF
        coverageJ
        coverageBP
        coverageMI
        coverageCA
        coverageSP
        coverageST
        coverageDR
        coveragePetBite
        coverageIBSC
        minimumDeductibleValue
        personalPropReimbursement
        coverageG
        coverageWaterBackup
        coverageMSALE
        coverageMS
        deductibleAllOther
      }
      global {
        affinity
        affinityGroupStem
        rateControlDate
        currentlyHomeInsured
        currentAutoAMBEST
        currentAutoCarrier
        currentAutoCarrierStartDate
        currentAutoCarrierEndDate
        currentAutoCarrierPolicyNumber
        continuousAutoCoverageStartDate
        mostRecentAutoInsuranceCoverageEndDate
        manualInventorySubmission
        discountInventoryScore
        discountDriverScore
        discountPaperless
        discountBixMutualAffinity
        homeownersPaymentType
        homeownersPaymentMethod
        autoPaymentType
        autoPaymentMethod
        condoPaymentType
        condoPaymentMethod
        currentlyAutoInsured
        homeEffectiveDate
        autoEffectiveDate
        homeSecurityPartnerCustomerType
        employeeDiscount
        autoMegaDownPay
        homeMegaDownPay
        rentersMegaDownPay
        condoMegaDownPay
        ## farm discounts (#34739)
        ## The policy Details screen renders these straight out of Formik: farm-discounts.jsx reads
        ## values.global.<field> for each row's applied state and for the card's "n of m" badge, and
        ## AutoBundleSelect reads multiPolicyDiscount + autoBundleCarrier. Omitting them here made
        ## every row read undefined, so a discount the segment already carried — and the premium
        ## already reflected — still showed a "+" and could be added over and over. They have always
        ## been written back (sanitize.js includes them in PolicyDetailsInput); only the read was
        ## missing. Kept in step with the card by policy.query.fragments.test.js.
        multiPolicyDiscount
        autoBundleCarrier
        legacyDiscount
        militaryDiscount
        educationDiscount
        traditionsDiscount
        farmAssociationDiscount
        memberOfCattlemenAssociation
        memberOfSoybeanAssociation
        cropInsuranceDiscount
        claimFreeDiscount
        ## cv data
        receivedCVData
        hadInforceCVPolicy
        priorIndividualBILimit
        priorOccurrenceBILimit
        priorUmIndividualBodilyInjury
        priorUmOccurrenceBodilyInjury
        priorDrivers {
          firstName
          lastName
        }
        priorCars {
          VIN
          year
          make
          model
          limitRental
          deductibleCollision
          deductibleComprehensive
          roadsideAssistance
        }
        # Farm policy restrictions — surfaced as a banner on the policy screen (#32036 G-06)
        farmVerification {
          restrictions
          # Change of Coverage — UW-only free-type notice trigger (#34275)
          changeOfCoverage
        }
      }
      rentersCoverage {
        rentersLocation {
          ...Address
          # #37133: an auto's attached renters location carries a county; without it the save dropped it.
          county
        }
        coverageCD
        coverageX
        coverageY
        deductible
      }
      farm {
        mortgageDetails {
          loanNumber
          mortgageHolderName
          mortgageHolderAddress {
            address
            address2
            country
            city
            state
            zip
          }
          mortgageHolderEmail
          mortgageHolderFax
          primary
          isCustom
        }
      }
      condo {
        constructionType
        condoLocation {
          ...Address
        }
        condoQuality
        mortgageDetails {
          loanNumber
          mortgageHolderName
          mortgageHolderAddress {
            address
            address2
            country
            city
            state
            zip
          }
          mortgageHolderEmail
          mortgageHolderFax
          primary
          isCustom
        }
        numFullBathrooms
        numHalfBathrooms
        numOccupants
        ownershipStatus
        purchaseDate
        purchaseAmount
        replacementCostEstimate {
          total
          itemized {
            calculatedValue
            type
            desc
            id
          }
          valuationId
        }
        roofShape
        sqFt
        typeOfCondo
        typeOfEntry
        hasBasement
        buildingHasMoreThanFiveUnits
        yearBuilt
        latitude
        longitude
        fpc
        county
        fireHydrantWithin1000ft
        windstormMitigationLevel
        fpcFromLocation
        fpcOverride
        fpcOverrideValue
        fpcOverrideReason
        fpcOverrideBy
        floor
        weeksRentedOut
        hasUnitNumber
        numVehicles
        dtoName
        dtbowName
        dtbowLabel
        wildfireHazardScore
        wildfireGallagherScore
      }
      cars {
        addDate
        VIN
        vehicleNumber
        year
        make
        model
        bodyType
        primaryUse
        purchaseDate
        garageLocation {
          ...Address
        }
        isGarageAddressRuleExceptionApproved
        deductibleCollision
        deductibleComprehensive
        limitUMPD
        limitRental
        limitACPE
        customEquipmentDescription
        limitIncomeLoss
        roadsideAssistance
        extendedNonOwnedCoverage
        solelyOwnedByApplicant
        statedAmount
        jointOwner
        jointOwnerName
        jointOwnerAddress {
          address
          address2
          city
          state
          zip
        }
        jointOwnerSameHousehold
        jointOwnerRelated
        jointOwnerRelationship
        ownership
        rideSharing
        umuimRejected
        confirmed
        aebStatus
        aebConfirmed
        antiTheftStatus
        antiTheftConfirmed
        blindSpotStatus
        blindSpotConfirmed
        singleAirbagStatus
        singleAirbagConfirmed
        doubleAirbagStatus
        doubleAirbagConfirmed
        coverageLoan
        assignedDriver
        userAssignedDriver
        symbolMake
        symbolModel
        symbolStyle
        symbolAux
        annualMilesDriven
        verifiedAnnualMilesDriven
        reportedAnnualMiles
        hasOutstandingRecall
        brandedTitle
        brandedTitleDescription
        rapaLiabilitySymbol1
        rapaLiabilitySymbol2
        rapaPhysDamSymbol1
        rapaPhysDamSymbol2
        rapaBodilyInjurySymbol
        rapaPropertyDamageSymbol
        rapaMedPaySymbol
        rapaComprehensiveSymbol
        rapaCollisionSymbol
      }
      drivers {
        ...People
      }
      nonDrivers {
        ...People
      }
      people {
        ...People
      }
      removedDrivers {
        ...People
      }
      trailers {
        VIN
        vehicleNumber
        type
        make
        model
        primaryUse
        year
        value
        contents
        purchaseDate
        garageLocation {
          ...Address
        }
        deductibleCollision
        deductibleComprehensive
        confirmed
        coverageLoan
        addDate
      }
      auto {
        householdMembers
        hasSetPip
        pipResidentsConfirmation
        pipEligibilityConfirmation
        pipResidentsWithQHC
        pipExcludedResidents
        pipAllResidents
        pipEveryoneOnSamePlan
        pipHealthInsuranceType
        pipAdditionalResidents
        residenceInsuranceType
        companionPolicyType
        companionPolicyNumber
        primaryResidentHomeowner
        adjustmentFactor
        adjustmentRationale
        importedDiscounts
        priorCoverageLapseDays
        priorCoverageMonthsInForce
      }
      home {
        basementSqFt
        basementType
        constructionType
        exteriorWallType
        garageCarCapacity
        garageType
        homeLocation {
          ...Address
        }
        homeQuality
        mortgageDetails {
          loanNumber
          mortgageHolderAddress {
            address
            address2
            country
            city
            state
            zip
          }
          mortgageHolderName
          primary
          mortgageHolderEmail
          mortgageHolderFax
          isCustom
        }
        numFamiliesInDwelling
        numFireplaces
        numFullBathrooms
        numHalfBathrooms
        numOccupants
        numStories
        numWoodBurningStoves
        ownershipStatus
        purchaseDate
        purchaseAmount
        replacementCostEstimate {
          total
          itemized {
            calculatedValue
            type
            desc
            id
          }
          valuationId
        }
        roofOriginal
        roofShape
        roofType
        roofYear
        roofDeck
        roofCover
        roofDeckAttachment
        roofToWallConnection
        openingProtection
        roofSWR
        sqFt
        originalSqFt
        swimmingPool
        typeOfHome
        yearBuilt
        latitude
        longitude
        county
        fireHydrantWithin1000ft
        porchCode
        porchSqFt
        hasTrampoline
        constructionStyle
        areasUnderConstruction
        fpc
        fpcFromLocation
        fpcOverride
        fpcOverrideValue
        fpcOverrideReason
        fpcOverrideBy
        wildfireHazardScore
        wildfireGallagherScore
        dtoName
        dtbowName
        dtbowLabel
        windstormMitigationLevel
        stormShutters
        fortifiedHome
      }
      connectedHome {
        monitored
        moistureDevices
        theftPreventionDevices
        motionDetectingDevices
        smokeDetectingDevices
        autoWaterShutoffDevices
        providerName
        agreeToTerms
      }
      multiPolicyDiscount
      underwritingChanges
      homeViolationDates
      homeSingleViolationDates
      homeClaimDates
      homeClaimsForUW
    }
  }
  ${Address}
  ${People}
  ${OFFER_FRAGMENT}
  ${FARM_LOCATIONS_FRAGMENT}
  ${LOSS_HISTORY_FRAGMENT}
  ${FARM_LIABILITY_COVERAGE_FRAGMENT}
`;

export const PolicyPreviewResponse = gql`
  fragment PolicyPreviewResponse on PolicyPreview {
    id
    estimateOnly
    changeDetails {
      effectiveDate
      updatedDateTime
      targetSegmentId
    }
    coverageChange
    itemsChanged
    itemsChangedAddendum
    billingDescription
    needsUnderwritingReview
    parentPreviewId
    originalPreviewId
    newPremium
    newTotalPrice
    originalTotalPrice
    internalNote
    uwAnswers {
      ...FarmVerification
    }
    clarionDoorIds {
      requestIdBeforePreview
      requestIdAfterPreview
    }
    feesChanged
    nextPayments {
      date
      remainingMonthly
      transactionFee
      total
    }
    diff
    skipImmediateBillOrRefund
    warning
    trailingDocChanges {
      timeStamp
      newValue
      oldValue
      addedChangeKey
      agent
      documentType
      newValueUM
      oldValueUM
      newValueUIM
      oldValueUIM
    }
    createdBy {
      username
      isAgency
    }
    policy {
      ...PolicyFragment
    }
  }
  ${PolicyFragment}
  ${FARM_VERIFICATION_FRAGMENT}
`;
